import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { registerUserScopedReset } from './session-read';
import type { NeedsGuesserScreen, NeedGuess, NeedsMetadata } from '../types';

// ============================================
// Type Definitions
// ============================================

interface NeedsGuesserState {
  currentScreen: NeedsGuesserScreen;
  inputSentence: string;
  inputContext: string;
  sessionId: string | null;
  needsGuesses: NeedGuess[];
  acceptedNeeds: NeedGuess[];
  rejectedNeeds: NeedGuess[];
  isLoading: boolean;
  error: string | null;
  metadata: NeedsMetadata;
}

interface NeedsGuesserActions {
  setInputSentence: (sentence: string) => void;
  setInputContext: (context: string) => void;
  reset: () => void;
  startOver: () => void;
  startGuessing: () => void;
  handleGuessResults: (needsGuesses: NeedGuess[], metadata: NeedsMetadata) => void;
  handleError: (errorMessage: string) => void;
  proceedToVisualize: () => void;
  backToRefine: () => void;
  toggleNeed: (need: NeedGuess) => void;
  isNeedAccepted: (need: NeedGuess) => boolean;
  rejectAllNeeds: () => void;
  acceptAllNeeds: () => void;
  setIsLoading: (isLoading: boolean) => void;
  clearError: () => void;
  isInputValid: () => boolean;
  getAcceptedCount: () => number;
  getRejectedCount: () => number;
  canProceedToVisualize: () => boolean;
  getBalloonColor: (universalNeed: string) => string;
  getNeedEmoji: (universalNeed: string) => string;
}

type NeedsGuesserStore = NeedsGuesserState & NeedsGuesserActions;

// ============================================
// Constants
// ============================================

const BALLOON_COLORS: Record<string, string> = {
  Subsistence: '#F87171',
  Protection: '#FB923C',
  Affection: '#FBBF24',
  Understanding: '#A3E635',
  Participation: '#34D399',
  Leisure: '#22D3EE',
  Creation: '#60A5FA',
  Identity: '#A78BFA',
  Freedom: '#F472B6',
};

const NEED_EMOJIS: Record<string, string> = {
  Subsistence: '🍎',
  Protection: '🛡️',
  Affection: '❤️',
  Understanding: '💡',
  Participation: '🤝',
  Leisure: '🎵',
  Creation: '🎨',
  Identity: '✨',
  Freedom: '🕊️',
};

// ============================================
// Store Implementation
// ============================================

export const useNeedsGuesserStore = create<NeedsGuesserStore>((set, get) => ({
  // State
  currentScreen: 'input',
  inputSentence: '',
  inputContext: '',
  sessionId: null,
  needsGuesses: [],
  acceptedNeeds: [],
  rejectedNeeds: [],
  isLoading: false,
  error: null,
  metadata: {
    sourceModel: null,
    latencyMs: null,
    // Legacy: tokensSpent is kept for backward compatibility with training data storage
    tokensSpent: null,
  },

  // Basic Actions
  setInputSentence: (sentence: string) => set({ inputSentence: sentence }),
  setInputContext: (context: string) => set({ inputContext: context }),

  reset: () =>
    set({
      currentScreen: 'input',
      inputSentence: '',
      inputContext: '',
      sessionId: null,
      needsGuesses: [],
      acceptedNeeds: [],
      rejectedNeeds: [],
      isLoading: false,
      error: null,
      metadata: {
        sourceModel: null,
        latencyMs: null,
        // Legacy: tokensSpent is kept for backward compatibility with training data storage
        tokensSpent: null,
      },
    }),

  startOver: () => {
    get().reset();
  },

  // Screen Transition Actions
  startGuessing: () => {
    const sessionId = uuidv4();
    set({
      currentScreen: 'processing',
      sessionId,
      isLoading: true,
      error: null,
    });
  },

  handleGuessResults: (needsGuesses: NeedGuess[], metadata: NeedsMetadata) => {
    set({
      currentScreen: 'refine',
      needsGuesses,
      acceptedNeeds: [...needsGuesses],
      rejectedNeeds: [],
      metadata,
      isLoading: false,
      error: null,
    });
  },

  handleError: (errorMessage: string) => {
    set({
      error: errorMessage,
      isLoading: false,
    });
  },

  proceedToVisualize: () => {
    set({
      currentScreen: 'visualize',
      error: null,
    });
  },

  backToRefine: () => {
    set({
      currentScreen: 'refine',
    });
  },

  // Refinement Actions
  toggleNeed: (need: NeedGuess) => {
    const { acceptedNeeds, rejectedNeeds } = get();

    const isAccepted = acceptedNeeds.some(
      (n) => n.universal_need === need.universal_need && n.underlying_need === need.underlying_need,
    );

    if (isAccepted) {
      set({
        acceptedNeeds: acceptedNeeds.filter(
          (n) =>
            !(
              n.universal_need === need.universal_need && n.underlying_need === need.underlying_need
            ),
        ),
        rejectedNeeds: [...rejectedNeeds, need],
      });
    } else {
      set({
        rejectedNeeds: rejectedNeeds.filter(
          (n) =>
            !(
              n.universal_need === need.universal_need && n.underlying_need === need.underlying_need
            ),
        ),
        acceptedNeeds: [...acceptedNeeds, need],
      });
    }
  },

  isNeedAccepted: (need: NeedGuess): boolean => {
    const { acceptedNeeds } = get();
    return acceptedNeeds.some(
      (n) => n.universal_need === need.universal_need && n.underlying_need === need.underlying_need,
    );
  },

  rejectAllNeeds: () => {
    const { needsGuesses } = get();
    set({
      acceptedNeeds: [],
      rejectedNeeds: [...needsGuesses],
    });
  },

  acceptAllNeeds: () => {
    const { needsGuesses } = get();
    set({
      acceptedNeeds: [...needsGuesses],
      rejectedNeeds: [],
    });
  },

  // Loading State Actions
  setIsLoading: (isLoading: boolean) => set({ isLoading }),
  clearError: () => set({ error: null }),

  // Utility Functions
  // UI requires 15 chars (short sentences give poor NVC analysis); backend accepts 3+ as a safety floor.
  isInputValid: (): boolean => {
    const { inputSentence } = get();
    return inputSentence.trim().length >= 15 && inputSentence.trim().length <= 500;
  },

  getAcceptedCount: (): number => {
    return get().acceptedNeeds.length;
  },

  getRejectedCount: (): number => {
    return get().rejectedNeeds.length;
  },

  canProceedToVisualize: (): boolean => {
    return get().acceptedNeeds.length > 0;
  },

  getBalloonColor: (universalNeed: string): string => {
    return BALLOON_COLORS[universalNeed] || '#9CA3AF';
  },

  getNeedEmoji: (universalNeed: string): string => {
    return NEED_EMOJIS[universalNeed] || '🎈';
  },
}));

// The sentence, its context and the guesses are the signed-in account's own
// (David, 2026-10-04): reset synchronously on every session change, with every
// per-user store (session-read.ts). The page's requests are session-bound too
// (needs-guesser.tsx), so a late answer for A never refills it for B.
registerUserScopedReset(() => useNeedsGuesserStore.getState().reset());
